/**
 * Utilities for parameter config tables that have parameter usages & want to render a "Confirmed in" column.
 */
import {
  ProcedureParameterConfirmationMethod,
  ProcedureParameterUsageValueType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  LastSyncedUpstreamValue,
  ProcedureParameterConfirmedInStructuredTableColumn,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {GetLsuvsForRowFn} from 'procedures/design/method-config/computeLSUVs';
import {PARAMETER_TABLES_COLUMN_WIDTHS} from 'procedures/design/ProcedureDesignConstants';
import {ParameterUsage, ParameterUsages} from 'procedures/ProcedureDomainTypes';
import {ParameterUsageJsonValue} from 'procedures/tables/ParameterUsageCell';
import {makeJsonCell} from 'procedures/tables/procedureTableAdapterUtils';

export interface BaseParameterConfigTableMetadata {
  navigateToStep: (stepCid: string) => void;
}

/**
 * Get parameter usage structured table column.
 */
export function getParameterUsageColumn({
  metadata,
  provenanceUuid,
  columnName = 'Confirmed in',
  columnGroup,
}: {
  metadata: BaseParameterConfigTableMetadata;
  provenanceUuid: string;
  columnName?: string;
  columnGroup?: ColumnGroup;
}): StructuredTableColumn {
  const usageColumn: ProcedureParameterConfirmedInStructuredTableColumn = {
    ...makeStructuredTableColumn({
      columnType: TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
      name: columnName,
      isRequired: false,
      width: PARAMETER_TABLES_COLUMN_WIDTHS.WIDE,
      isReadOnly: true,
    }),
    columnType: TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN,
    columnGroup,
    navigateToStep: metadata.navigateToStep,
    provenanceUuid,
  };
  return usageColumn;
}

/**
 * Construct json cell for parameter confirmed in column.
 * @param parameterUsages Parameter usages for the row
 * @param usageType Which usage type is relevant for the column; defaults to CONFIRMATION.
 * @param confirmationMethod Confirmation method for parameter (table or plate map); defaults to Table
 */
export function makeParameterUsageCell(
  parameterUsages: Array<ParameterUsage>,
  {
    usageType,
    confirmationMethod,
  }: {
    usageType?: ProcedureParameterUsageValueType;
    confirmationMethod?: ProcedureParameterConfirmationMethod;
  } = {}
): Cell {
  return makeJsonCell<ParameterUsageJsonValue>({
    parameterUsages,
    targetParameterUsageType: usageType ?? ProcedureParameterUsageValueType.CONFIRMATION,
    confirmationMethod: confirmationMethod ?? ProcedureParameterConfirmationMethod.TABLE,
  });
}

/**
 * Select parameter usages for the specific version id, or an empty list if it's not in the mapping.
 */
export function getParameterUsagesForId(
  parameterUsagesByParameterVersionId: ParameterUsages,
  parameterVersionId: string | null
): Array<ParameterUsage> {
  return parameterUsagesByParameterVersionId[parameterVersionId ?? ''] ?? [];
}

/**
 * Transform last synced upstream values function to pass in parameter usages.
 */
export function transformLsuvsForRowFn<TModel, TRowMetadata>(
  getLsuvsWithParamUsages: (
    model: TModel,
    row: StructuredTableRow<TRowMetadata>,
    columns: Array<StructuredTableColumn>,
    parameterUsages: Array<ParameterUsage>
  ) => Array<LastSyncedUpstreamValue>,
  parameterUsagesById: ParameterUsages,
  getVersionId: (model: TModel) => string | null
): GetLsuvsForRowFn<TModel, TRowMetadata> {
  return (model, row, column) =>
    getLsuvsWithParamUsages(
      model,
      row,
      column,
      getParameterUsagesForId(parameterUsagesById, getVersionId(model))
    );
}
